import { Utils, C } from '@kineticdata/bundle-common';
import { List, Map } from 'immutable';

export const Form = (o = {}) =>
  o && o._model
    ? o
    : {
        name: o.name,
        slug: o.slug,
        description: o.description,
        type: o.type,
        status: o.status,
        icon: Utils.getIcon(
          o,
          o.type === 'Approval'
            ? 'clipboard-check'
            : o.type === 'Action'
              ? 'tasks'
              : 'ticket-alt',
        ),
        image: Utils.getAttributeValue(
          o,
          C.ATTR_IMAGE,
          Utils.getAttributeValue(
            o.kapp || {},
            C.ATTRIBUTE_KAPP_DEFAULT_FORM_IMAGE,
          ),
        ),
        keywords: Utils.getAttributeValues(o, C.ATTR_KEYWORD),
        categories:
          o.categorizations && o.categorizations.map(c => c.category.slug),
        fundingDependent: Utils.hasAttributeValue(
          o,
          C.FORM_ATTR_FUNDING_DEPENDENT,
          'True',
          true,
        ),
        hasApprovals: Utils.hasAttributeValue(
          o,
          C.FORM_ATTR_APPROVALS,
          'True',
          true,
        ),
        trackedDuplicateButton: Utils.getAttributeValue(
          o,
          C.FORM_ATTR_TRACKED_DUPLICATE_BUTTON,
          null,
        ),
        isPortalPageLink: Utils.hasAttributeValue(
          o,
          C.FORM_ATTR_IS_MENU_LINK,
          'True',
          true,
        ),
        portalPageGroupName: Utils.getAttributeValue(
          o,
          C.FORM_ATTR_PAGE_GROUP_NAME,
          '',
        ),
        portalPageSortOrder:
          parseInt(
            Utils.getAttributeValue(o, C.FORM_ATTR_PAGE_SORT_ORDER, '100000'),
            10,
          ) || 100000,
        isInternal: !!Utils.getAttributeValue(o, C.FORM_ATTR_INTERNAL),
        maintenance: Utils.getAttributeValue(o, C.FORM_ATTR_MAINTENANCE),
        createdAt: o.createdAt,
        updatedAt: o.updatedAt,
        kapp: o.kapp,
        _model: true,
      };

export const Category = categoryHelper => category => {
  const forms = category.categorizations
    ? List(
        category.categorizations.map(categorization =>
          Form(categorization.form),
        ),
      ).sortBy(form => form.name)
    : List();
  const servicesForms = forms.filter(
    form =>
      C.REQUEST_FORM_TYPE === form.type &&
      C.REQUEST_FORM_STATUSES.includes(form.status),
  );
  return {
    name: category.name,
    slug: category.slug,
    sortOrder: parseInt(
      Utils.getAttributeValue(category, C.ATTR_SORT_ORDER, 1000),
      10,
    ),
    icon: Utils.getIcon(category, C.ICON_SERVICE),
    hidden:
      Utils.getAttributeValue(
        category,
        C.ATTR_HIDDEN,
        'false',
      ).toLowerCase() === 'true',
    parentSlug: Utils.getAttributeValue(category, C.ATTR_PARENT),
    allForms: forms,
    forms: servicesForms,
    formCount: servicesForms.size,
    hasParent() {
      return categoryHelper.hasCategory(this.parentSlug);
    },
    getParent() {
      return categoryHelper.getCategory(this.parentSlug);
    },
    hasChildren() {
      return categoryHelper.hasChildren(this.slug);
    },
    getChildren() {
      return categoryHelper.getChildren(this.slug);
    },
    getDescendants() {
      return categoryHelper.getDescendants(this.slug);
    },
    getTotalFormCount() {
      return this.getDescendants().reduce(
        (count, category) => count + category.formCount,
        this.formCount,
      );
    },
    isEmpty() {
      return (
        this.formCount === 0 &&
        !this.getDescendants().some(category => category.formCount > 0)
      );
    },
    getTrail() {
      return categoryHelper.getTrail(this);
    },
    getFullSortOrder() {
      return this.getTrail()
        .map(category => `${category.sortOrder}`.padStart(4, '0'))
        .join('.');
    },
  };
};

export const CategoryHelper = (categories = [], includeHidden = false) => {
  const helper = {
    getCategories() {
      return this.categories
        .filter(includeHidden ? c => c : c => !c.hidden)
        .toList()
        .sortBy(category => category.getFullSortOrder());
    },
    getRootCategories() {
      return this.categories
        .filter(category => !category.hasParent())
        .filter(includeHidden ? c => c : c => !c.hidden)
        .toList()
        .sortBy(category => category.getFullSortOrder());
    },
    hasCategory(slug) {
      return this.categories.has(slug);
    },
    getCategory(slug) {
      return this.categories.get(slug);
    },
    hasChildren(slug) {
      return this.categories.some(category => category.parentSlug === slug);
    },
    getChildren(slug) {
      return this.categories
        .filter(category => category.parentSlug === slug)
        .filter(includeHidden ? c => c : c => !c.hidden)
        .toList()
        .sortBy(category => category.getFullSortOrder());
    },
    getDescendants(slug) {
      return this.getChildren(slug)
        .flatMap(category => [category, ...this.getDescendants(category.slug)])
        .filter(includeHidden ? c => c : c => !c.hidden)
        .toList()
        .sortBy(category => category.getFullSortOrder());
    },
    getTrail(category, trail = List()) {
      return category
        ? this.getTrail(category.getParent(), trail.unshift(category))
        : trail;
    },
    getForms() {
      return this.forms;
    },
  };
  helper.categories = List(categories)
    .map(Category(helper))
    // .filter(includeHidden ? c => c : c => !c.hidden)
    .toOrderedMap()
    .mapKeys((key, category) => category.slug);
  helper.forms = categories
    .reduce((forms, category) => {
      return (category.categorizations || []).reduce(
        (catForms, categorization) =>
          catForms.set(categorization.form.slug, Form(categorization.form)),
        forms,
      );
    }, Map())
    .toList()
    .filter(
      form =>
        C.REQUEST_FORM_TYPE === form.type &&
        C.REQUEST_FORM_STATUSES.includes(form.status),
    )
    .sortBy(form => form.name);
  return helper;
};
